import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import "./Grade12MathLesson.css";  // You can reuse the same CSS or create a new one.

import { awardToken } from "../../awardtoken"; // or wherever it's located

// Reuse or adapt these tools as needed:
import Calculator from "../MathTools/Calculator";
import WritingPad from "../MathTools/WritingPad";
import drawGrid from "../MathTools/drawGrid";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg";
import { chapterPages } from "./Grade12chapterPages";
import { getFinalExamQuestions } from "./Grade12FinalQuestion";
import { getQuestionPool } from "./Grade12QuestionPool"

/* ----------------------------------------------------------
   1) CHAPTER MAPPING
   Each chapter => short set of slides => then quiz => next
---------------------------------------------------------- */
const chapterTopicMap = {
  1: "advancedFunctions",
  2: "polynomialFunctions",
  3: "rationalFunctions",
  4: "exponentialLog",
  5: "trigIdentities",
  6: "sinusoidalFunctions",
  7: "advancedEquations",
  8: "combinatorics",
  9: "probabilityStats",
  10: "calcIntro"
};


/* ----------------------------------------------------------
   5) Main Grade12MathLesson component
   — Follows the same structure as Grade11MathLesson
---------------------------------------------------------- */
export default function Grade12MathLesson(onAwardTokens) {
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);

  const [showChapterQuiz, setShowChapterQuiz] = useState(false);
  const [chapterQuiz, setChapterQuiz] = useState([]);
  const [chQidx, setChQidx] = useState(0);
  const [chScore, setChScore] = useState(0);
  const [chFin, setChFin] = useState(false);

  const [allChaptersDone, setAllChaptersDone] = useState(false);

  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fDone, setFDone] = useState(false);

  const [finalQuizAttempted, setFinalQuizAttempted] = useState(false);

  // A flag to see if they've previously finished the entire Grade 12 lesson
const [hasCompletedBefore, setHasCompletedBefore] = useState(false);

useEffect(() => {
  // On first mount, check if localStorage says "complete"
  const completedBefore = localStorage.getItem('grade12MathComplete');
  if (completedBefore === 'true') {
    setHasCompletedBefore(true);
    setAllChaptersDone(true); 
    // This ensures we skip the normal linear flow
    // so they can jump to any chapter or final quiz
  }
}, []);

useEffect(() => {
  // Whenever user truly finishes all chapters, store that in localStorage
  if (allChaptersDone) {
    localStorage.setItem('grade12MathComplete','true');
  }
}, [allChaptersDone]);

  // toggles for tools
  const [showCalc, setShowCalc] = useState(false);
  const [showPad, setShowPad] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [gridPoints, setGridPoints] = useState([]);

  const gridCanvasRef = useRef(null);
  useEffect(() => {
    if (!showGrid || !gridCanvasRef.current) return;
    drawGrid(gridCanvasRef.current, { widthCells:10, heightCells:10, cellSize:20 }, gridPoints);
  }, [showGrid, gridPoints]);

  function handleGridClick(e){
    const rect = gridCanvasRef.current.getBoundingClientRect();
    const cellSize=20;
    const widthCells=10, heightCells=10;
    const halfW=(widthCells/2)*cellSize;
    const halfH=(heightCells/2)*cellSize;

    const xPx=e.clientX-rect.left;
    const yPx=e.clientY-rect.top;

    const xCell=Math.round((xPx - halfW)/cellSize);
    const yCell=Math.round((halfH - yPx)/cellSize);

    setGridPoints(ps=>[...ps, {x:xCell,y:yCell}]);
  }

  // user info (similar to Grade11)
  const [user, setUser] = useState(null);
  useEffect(()=>{
    const logged = JSON.parse(localStorage.getItem("loggedInUser"));
    if(logged) setUser(logged);
  },[]);

  // pages for current chapter
  const pages = chapterPages[chapter];
  const currentPage = pages[pageIdx];
  const isLastPage = pageIdx === pages.length-1;

  // total slides
  const totalSlides = useMemo(()=>{
    return Object.values(chapterPages).reduce((acc,arr)=>acc+arr.length,0);
  },[]);
  // slides so far
  const slidesSoFar = useMemo(()=>{
    let sum=0;
    for(let c=1; c<chapter; c++){
      sum += chapterPages[c].length;
    }
    sum += pageIdx;
    return sum;
  },[chapter,pageIdx]);
  const progress = Math.round(((slidesSoFar+1)/totalSlides)*100);

  function nextPage(){
    if(pageIdx<pages.length-1){
      setPageIdx(pageIdx+1);
    }
  }
  function prevPage(){
    if(pageIdx>0){
      setPageIdx(pageIdx-1);
    }
  }

  // quiz
  function startChapterQuiz(){
    const topic=chapterTopicMap[chapter];
    if(!topic){
      setAllChaptersDone(true);
      return;
    }
    const pool = getQuestionPool(topic);
    const used=new Set();
    const quizArr=[];
    const quizCount=Math.min(5,pool.length);
    while(quizArr.length<quizCount){
      const item=pool[Math.floor(Math.random()*pool.length)];
      const key=item.question.toString();
      if(!used.has(key)){
        used.add(key);
        quizArr.push(item);
      }
      if(used.size===pool.length) break;
    }
    setChapterQuiz(quizArr);
    setChQidx(0);
    setChScore(0);
    setChFin(false);
    setShowChapterQuiz(true);
  }
  function handleChAnswer(opt){
    if(String(opt)===String(chapterQuiz[chQidx].question().a)){
      setChScore(s=>s+1);
    }
    if(chQidx<chapterQuiz.length-1){
      setChQidx(i=>i+1);
    } else {
      setChFin(true);
    }
  }
  function finishChapterQuiz(){
    const total=chapterQuiz.length;
    const needed=Math.ceil(total*0.6);
    if(chScore>=needed){
      if(chapter<10){
        setChapter(c=>c+1);
        setPageIdx(0);
      } else {
        setAllChaptersDone(true);
      }
      setShowChapterQuiz(false);
    } else {
      alert(`Score below 60% (${chScore}/${total}). Please review and try again.`);
      startChapterQuiz();
    }
  }

  // final quiz
  function startFinalQuiz(){
    if(finalQuizAttempted) return;
    const finalArr=getFinalExamQuestions();
    setFinalQs(finalArr);
    setFIdx(0);
    setFScore(0);
    setFDone(false);
    setShowFinal(true);
    setFinalQuizAttempted(true);
  }
  function handleFinalAns(opt){
    if(String(opt)===String(finalQs[fIdx].question().a)){
      setFScore(s=>s+1);
    }
    if(fIdx<finalQs.length-1){
      setFIdx(i=>i+1);
    } else {
      setFDone(true);
    }
  }

function CompletedMenu() {
    // Let them pick a chapter from 1..10 or do final quiz
    const chapterList = [1,2,3,4,5,6,7,8,9,10];
  
    function handleChapterSelect(ch) {
      setChapter(ch);
      setPageIdx(0);
      setShowChapterQuiz(false);
      // If you want to force them back into normal linear mode, setAllChaptersDone(false)
      // But presumably they can just read or quiz again freely:
    }
  
    function handleRetakeFinal() {
      startFinalQuiz();
    }
  
    return (
      <div style={{ textAlign:"center", margin:"2em 0" }}>
        <h2>Welcome Back!</h2>
        <p>
          You’ve previously completed all Grade&nbsp;12 Math chapters. 
          Feel free to jump to any chapter for review, or retake the final quiz.
        </p>
        <div style={{ marginBottom:"1em" }}>
          <label style={{ marginRight:8 }}>Jump to Chapter:</label>
          <select onChange={(e)=>handleChapterSelect(Number(e.target.value))}>
            <option value="">--Select--</option>
            {chapterList.map(ch=>(
              <option key={ch} value={ch}>Chapter {ch}</option>
            ))}
          </select>
        </div>
  
        <button className="button1" onClick={handleRetakeFinal}>
          Retake Final Quiz
        </button>
      </div>
    );
  }
  
  /* Sub UIs */
  function LessonUI(){
    return (
      <>
        <h2 className="lesson-page-title">{currentPage.title}</h2>
        <div className="lesson-page-content">{currentPage.body}</div>
        <div className="lesson-page-navigation">
          {pageIdx>0 && (
            <button className="button1" onClick={prevPage}>◀ Previous</button>
          )}
          {pageIdx<pages.length-1 && (
            <button className="button1" onClick={nextPage}>Next ▶</button>
          )}
        </div>
        {isLastPage && (
          <button className="button1" onClick={startChapterQuiz}>
            Take Chapter {chapter} Quiz
          </button>
        )}
        <br/>
        <Link to="/math" className="grade11-back">← Back to Math Page</Link>
      </>
    );
  }

  function ChapterQuizUI(){
    const current=chapterQuiz[chQidx];
    const safeQObj = useMemo(() => {
        return current
          ? current.question()
          : { q: "", a: "", distract: [] };
      }, [current]);

    const allOptions=useMemo(()=>{
      if(!safeQObj.a) return [];
      const unique=new Set([safeQObj.a,...(safeQObj.distract||[])]);
      return [...unique].sort(()=>Math.random()-0.5);
    },[safeQObj]);
    let Illustration = current?.illustration || null;
    if(Illustration){
      Illustration=React.cloneElement(Illustration,{draggable:false,showStats:false});
    }
    if(!current){
      return <p>Loading quiz...</p>;
    }
    if(!chFin){
      return (
        <div className="quick-quiz-story-mode">
          <div className="quiz-top-panel">
            <div className="teacher-image-container">
              <img src="/images/Teacher.png" alt="Teacher" className="quiz-character-img" />
            </div>
            <div className="quiz-question-box">
              {Illustration && (
                <div className="quiz-illustration" style={{ marginBottom:"1em" }}>
                  {Illustration}
                </div>
              )}
              <div
                className="quiz-question-text"
                dangerouslySetInnerHTML={{__html: safeQObj.q}}
              />
            </div>
          </div>
          <div className="quiz-bottom-panel">
            <div className="quiz-answer-box-left">
              {allOptions.map((opt,i)=>(
                <button
                  key={i}
                  className="quiz-answer-btn"
                  onClick={()=>handleChAnswer(opt)}
                  dangerouslySetInnerHTML={{__html: opt}}
                />
              ))}
              <p className="quiz-score-display">
                Score: {chScore}/{chapterQuiz.length}
              </p>
              <button className="button1" onClick={()=>setShowCalc(c=>!c)}>
                {showCalc?"Hide Calculator":"Show Calculator"}
              </button>
              <button className="button1" onClick={()=>setShowPad(p=>!p)}>
                {showPad?"Hide Writing Pad":"Show Writing Pad"}
              </button>
              <button className="button1" onClick={()=>setShowGrid(g=>!g)}>
                {showGrid?"Hide Grid":"Show Grid"}
              </button>
              {showGrid && (
                <>
                  <button className="button1" onClick={()=>setGridPoints([])}>
                    Clear Points
                  </button>
                  <canvas
                    ref={gridCanvasRef}
                    width={10*20}
                    height={10*20}
                    onClick={handleGridClick}
                    style={{
                      border:"1px solid #444",
                      marginTop:8,
                      cursor:"crosshair",
                      display:"block",
                      margin:"0.5em auto"
                    }}
                  />
                </>
              )}
              {showCalc && <Calculator />}
              {showPad && <WritingPad />}
            </div>
            <div className="student-image-container">
              <img
                src={user?.avatar||defaultProfile}
                alt="Student"
                className="quiz-character-img-student"
              />
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="quiz-result-screen">
          <h3>Chapter {chapter} Quiz Finished!</h3>
          <p>You scored {chScore}/{chapterQuiz.length}.</p>
          <button className="button1" onClick={finishChapterQuiz}>
            Continue
          </button>
        </div>
      );
    }
  }

  function FinalQuizUI(){
    const current=finalQs[fIdx];
    const hasAwardedTokens = useRef(false);
    const safeQObj = useMemo(() => {
  return current
    ? current.question()
    : { q: "", a: "", distract: [] };
}, [current]);

    const allOptions=useMemo(()=>{
      if(!safeQObj.a) return [];
      const unique=new Set([safeQObj.a,...(safeQObj.distract||[])]);
      return [...unique].sort(()=>Math.random()-0.5);
    },[safeQObj]);
    let Illustration=current?.illustration||null;
    if(Illustration){
      Illustration=React.cloneElement(Illustration,{draggable:false,showStats:false});
    }
    if(!current){
      return <p>Loading final quiz...</p>;
    }
    if(!fDone){
      return (
        <div className="quick-quiz-story-mode">
          <div className="quiz-top-panel">
            <div className="teacher-image-container">
              <img src="/images/teacher.png" alt="Teacher" className="quiz-character-img" />
            </div>
            <div className="quiz-question-box">
              {Illustration && (
                <div className="quiz-illustration" style={{marginBottom:"1em"}}>
                  {Illustration}
                </div>
              )}
              <div
                className="quiz-question-text"
                dangerouslySetInnerHTML={{__html: safeQObj.q}}
              />
            </div>
          </div>
          <div className="quiz-bottom-panel">
            <div className="quiz-answer-box-left">
              {allOptions.map((opt,i)=>(
                <button
                  key={i}
                  className="quiz-answer-btn"
                  onClick={()=>handleFinalAns(opt)}
                  dangerouslySetInnerHTML={{__html: opt}}
                />
              ))}
              <p className="quiz-score-display">
                Score: {fScore}/{finalQs.length}
              </p>
              <button className="button1" onClick={()=>setShowCalc(c=>!c)}>
                {showCalc?"Hide Calculator":"Show Calculator"}
              </button>
              <button className="button1" onClick={()=>setShowPad(p=>!p)}>
                {showPad?"Hide Writing Pad":"Show Writing Pad"}
              </button>
              {showCalc && <Calculator />}
              {showPad && <WritingPad />}
            </div>
            <div className="student-image-container">
              <img
                src={user?.avatar||defaultProfile}
                alt="Student"
                className="quiz-character-img-student"
              />
            </div>
          </div>
        </div>
      );
    } else {
        if(!hasAwardedTokens.current){
            // 1) Check if user got ≥80%
            const percent = (fScore / finalQs.length)*100;
            if (percent >= 80) {
              awardToken();  // legacy—updates localStorage (if needed by other code)
              if (onAwardTokens) onAwardTokens(1);  // this notifies App.js to increment navbar!
              const updated = JSON.parse(localStorage.getItem("loggedInUser"));
              setUser(updated);
            }
            const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
            quizResults["math_g12"] = {
                ...(quizResults["math_g12"] || {}),
                  finalQuizScore: fScore,
                    finalQuizTotal: finalQs.length,
                      lastCompleted: Date.now()
                    };
            localStorage.setItem("quizResults", JSON.stringify(quizResults));
            hasAwardedTokens.current = true; // so we do it once
          }
      return (
        <div className="quiz-result-screen">
          <h3>Final Quiz Finished!</h3>
          <p>You scored {fScore}/{finalQs.length}!</p>
          <button className="button1" onClick={()=>setShowFinal(false)}>
            Return to Lesson
          </button>
        </div>
      );
    }
  }

  return (
    <div className="grade12-math-page">
      <div className="grade12-math-page-scale">
        {/* progress bar */}
        <div style={{ margin:"10px 0" }}>
          <div
            style={{
              width:"80%",
              margin:"0 auto",
              background:"#ddd",
              borderRadius:10,
              height:20
            }}
          >
            <div
              style={{
                width:`${progress}%`,
                background:"#4caf50",
                height:20,
                transition:"width 0.3s"
              }}
            />
          </div>
          <p>{progress}% complete</p>
        </div>

        {/* If they've completed before AND they're not currently in the final quiz */}
         {hasCompletedBefore && !showFinal && !showChapterQuiz && (
        <CompletedMenu />
      )}
        {/* If not all chapters done => show lesson or quiz */}
        {!allChaptersDone && !showFinal && (
          <>
            {!showChapterQuiz ? <LessonUI/> : <ChapterQuizUI/>}
          </>
        )}

        {/* If done all chapters => final quiz button */}
        {allChaptersDone && !showFinal && !finalQuizAttempted && (
          <div style={{ textAlign:"center" }}>
            <h2>All Chapters Completed!</h2>
            <p>Now you can take the 50-question final exam on Grade 12 topics.</p>
            <button className="button1" onClick={startFinalQuiz}>
              Start Final Quiz
            </button>
          </div>
        )}

        {/* Final quiz UI */}
        {showFinal && <FinalQuizUI/>}
      </div>
    </div>
  );
}
